import React from 'react';
import { Link } from 'react-router-dom';
import { FiEye, FiX, FiCheckSquare, FiTarget, FiUsers, FiAlertTriangle } from 'react-icons/fi';
import * as sched from 'sched';
import { useWatchlist } from '@/hooks/useWatchlist';
import { useDatabase } from '@/hooks/useDatabase';
import { useWorkspace } from '@/context/WorkspaceContext';
import type { WorkspaceUrlTarget } from '@/routes/workspaceUrl';

interface WatchlistPanelProps {
  workspaceId: string | undefined;
}

interface ResolvedRow {
  target: sched.WatchTarget;
  title: string;
  href: string | null; // null when deleted and no navigation target remains
  deleted: boolean;
  icon: React.ReactNode;
}

/**
 * Resolve a watch target to a displayable row. Deleted items fall back to
 * the snapshot captured at deletion time; live items resolve via the WASM
 * database. Unresolved-but-not-snapshot items (e.g. data not yet loaded)
 * display a muted placeholder.
 */
function resolveRow(
  watch: sched.Watch,
  buildUrl: (target: WorkspaceUrlTarget) => string,
  database: ReturnType<typeof useDatabase>['database'],
): ResolvedRow {
  if (watch.deleted_snapshot) {
    return {
      target: watch.target,
      title: watch.deleted_snapshot.title,
      href: null,
      deleted: true,
      icon: <FiAlertTriangle className="w-4 h-4 text-cm-text-muted" />,
    };
  }

  const id = watch.target.id;
  let title = id;
  let href: string | null = null;
  let icon: React.ReactNode = null;

  if (watch.target.type === 'task') {
    const task = database?.tasks()[id];
    title = task ? task.title() : id;
    href = buildUrl({ kind: 'task', taskId: id });
    icon = <FiCheckSquare className="w-4 h-4 text-cm-accent-blue" />;
  } else if (watch.target.type === 'outcome') {
    const outcome = database?.outcomes()[id];
    title = outcome ? outcome.title() : id;
    href = buildUrl({ kind: 'outcome', outcomeId: id });
    icon = <FiTarget className="w-4 h-4 text-cm-accent-orange" />;
  } else if (watch.target.type === 'team') {
    const team = database?.teams()[id];
    title = team ? team.name() : id;
    href = buildUrl({ kind: 'team', teamId: id });
    icon = <FiUsers className="w-4 h-4 text-cm-accent-green" />;
  }

  return { target: watch.target, title, href, deleted: false, icon };
}

const WatchlistPanel: React.FC<WatchlistPanelProps> = ({ workspaceId }) => {
  const { watches, loading, error, removeWatch } = useWatchlist(workspaceId);
  const { database } = useDatabase();
  const { buildUrl } = useWorkspace();

  const rows: ResolvedRow[] = watches
    .map((w) => resolveRow(w, buildUrl, database))
    .sort((a, b) => a.title.localeCompare(b.title));

  return (
    <div className="rounded-xl border border-cm-border-subtle bg-cm-bg-surface p-6">
      <div className="flex items-center gap-2 mb-4">
        <FiEye className="w-5 h-5 text-cm-accent-teal" />
        <h2 className="font-mono font-medium text-cm-text-primary">Watched Items</h2>
        <span className="text-xs font-mono text-cm-text-muted ml-auto">
          {rows.length} {rows.length === 1 ? 'item' : 'items'}
        </span>
      </div>

      {error && (
        <p className="text-sm text-red-400 mb-3" role="alert">
          {error}
        </p>
      )}

      {loading && rows.length === 0 ? (
        <p className="text-sm text-cm-text-muted text-center py-4">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-cm-text-muted text-center py-4">
          No watched items yet. You can watch tasks, outcomes, and teams from their detail pages.
        </p>
      ) : (
        <ul className="divide-y divide-cm-border-subtle">
          {rows.map((row) => (
            <li
              key={`${row.target.type}-${row.target.id}`}
              className="flex items-center gap-3 py-2 first:pt-0 last:pb-0"
            >
              <span className="shrink-0">{row.icon}</span>
              <div className="min-w-0 flex-grow">
                {row.href && !row.deleted ? (
                  <Link
                    to={row.href}
                    className="text-sm text-cm-text-primary hover:text-cm-accent-teal transition-colors truncate block"
                  >
                    {row.title}
                  </Link>
                ) : (
                  <span className="text-sm text-cm-text-muted italic line-through truncate block">
                    {row.title} {row.deleted && '(deleted)'}
                  </span>
                )}
                <span className="text-[10px] font-mono text-cm-text-muted uppercase tracking-wide">
                  {row.target.type}
                </span>
              </div>
              <button
                onClick={() => removeWatch(row.target)}
                className="shrink-0 text-cm-text-muted hover:text-red-400 p-1 rounded hover:bg-cm-bg-hover transition-colors cursor-pointer"
                aria-label="Remove from watchlist"
              >
                <FiX className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default WatchlistPanel;
